import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, RotateCcw } from 'lucide-react'
import type { FormSchema } from '@kfolks/types'
import { Button } from '@/shared/components/atoms/ui/button'
import { canonicalEquals } from '@/modules/shared/json-canonicalize'
import { useFormBuilderState } from './FormBuilderState'

/**
 * Raw JSON editor for the form builder, wired to the shared
 * `FormBuilderState` so edits round-trip with the Visual / Preview tabs.
 *
 * Per the form-builder capability spec + design D12, the Raw JSON tab is
 * always reachable. This component is the **state-bridged** editor that
 * lives under that tab — the standalone Spec 1 `FormSchemaEditorPage`
 * has been retired (its load/save cycle didn't talk to the builder
 * context, so switching tabs lost unsaved Visual edits).
 *
 * **Live commit on valid JSON**: every keystroke is parsed; if it produces
 * a structurally-valid `FormSchema` object, it's committed to the shared
 * state immediately. Switching to Visual then sees the JSON edits without
 * an explicit "apply" step. Invalid JSON keeps the textarea in a "draft"
 * state — the shared state is unchanged until the admin types valid JSON
 * again. Parse errors surface inline below the textarea.
 *
 * **No autosave to the server**: this tab does not call
 * `PUT /requests/types/:id`. Save is a builder-wide action (task 7.9) that
 * any tab can trigger; pressing it commits whatever's currently in shared
 * state. Authors who want to abandon Raw JSON edits use **Reset to current**
 * to roll the textarea back to the shared state's canonical form.
 *
 * **Round-trip canonicalization**: when the shared state changes externally
 * (e.g., from the Visual tab), and the textarea isn't actively being
 * edited, the textarea reflows to the shared state via
 * `canonicalEquals` (so trivial whitespace differences don't fight the
 * admin's draft).
 */
export function RawJsonEditor() {
	const { formSchema, setFormSchema } = useFormBuilderState()
	const [draft, setDraft] = useState<string>(() => stringify(formSchema))

	// Track which shared-state schema the textarea was last reflected from.
	// Reflow rule: if the admin hasn't typed since the last reflow (i.e.,
	// `draft` still canonicalises to the value we wrote it from), reflow on
	// every external change. Once the admin types, `draft` diverges and we
	// leave it alone until they `Reset to current` or commit valid JSON.
	const lastReflectedRef = useRef<FormSchema>(formSchema)

	// Reflow on external schema changes only (not on `draft` changes — that
	// would fight the admin's typing). Reading `draft`/`setDraft` from the
	// closure is intentional; the repo's ESLint config doesn't enforce
	// `react-hooks/exhaustive-deps`, so we don't need a disable comment.
	useEffect(() => {
		const parsed = safeParse(draft)
		if (parsed.ok && canonicalEquals(parsed.value, lastReflectedRef.current)) {
			// Admin hasn't diverged — refresh the canonical text from the new state.
			setDraft(stringify(formSchema))
			lastReflectedRef.current = formSchema
		}
		// If draft is invalid or diverged, leave it alone — admin is editing.
	}, [formSchema])

	const parseStatus = useMemo(() => safeParse(draft), [draft])

	// Push valid drafts into shared state when the canonical form differs
	// from current. Skips no-op updates so we don't churn the provider.
	// `formSchema`/`setFormSchema` are intentionally read from closure.
	useEffect(() => {
		if (parseStatus.ok && !canonicalEquals(parseStatus.value, formSchema)) {
			setFormSchema(parseStatus.value)
			lastReflectedRef.current = parseStatus.value
		}
	}, [parseStatus])

	const reset = () => {
		setDraft(stringify(formSchema))
		lastReflectedRef.current = formSchema
	}

	return (
		<div className="space-y-3" aria-label="Raw JSON editor">
			<div className="flex items-center justify-between gap-2">
				<p className="text-sm text-muted-foreground">
					Edits here flow to the Visual + Preview tabs. Save from anywhere persists the result.
				</p>
				<Button variant="outline" size="sm" onClick={reset} aria-label="Reset to current schema">
					<RotateCcw className="size-4" /> Reset to current
				</Button>
			</div>

			<textarea
				aria-label="Form schema JSON"
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				rows={24}
				spellCheck={false}
				className={
					'w-full rounded-md border bg-background p-3 font-mono text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
					(parseStatus.ok ? 'border-input' : 'border-destructive')
				}
			/>

			{!parseStatus.ok ? (
				<div
					role="alert"
					className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive"
				>
					<AlertCircle className="mt-0.5 size-4 shrink-0" />
					<div>
						<p className="font-medium">JSON parse error</p>
						<p className="mt-0.5 text-xs">{parseStatus.error}</p>
						<p className="mt-1 text-xs">
							Edits remain in this textarea (draft) — the Visual tab will not see them until the JSON is valid again.
						</p>
					</div>
				</div>
			) : null}
		</div>
	)
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function stringify(schema: FormSchema): string {
	return JSON.stringify(schema, null, 2)
}

type ParseStatus = { ok: true; value: FormSchema } | { ok: false; error: string }

/**
 * Parse + minimal shape-check. We deliberately do NOT run
 * `validateFormSchema` here (that's the debounced live-validation path,
 * task 7.8). Goal: accept anything that's a structural `FormSchema`
 * (object with `version: '1.0'` and an array of fields) so the Visual tab
 * can render it; deeper invariants (duplicate ids, missing dataSource)
 * surface as inline validation errors elsewhere, not as JSON parse errors.
 */
function safeParse(text: string): ParseStatus {
	let parsed: unknown
	try {
		parsed = JSON.parse(text)
	} catch (err) {
		return { ok: false, error: err instanceof Error ? err.message : 'Invalid JSON' }
	}
	if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
		return { ok: false, error: 'Top-level value must be an object' }
	}
	const obj = parsed as Record<string, unknown>
	if (obj.version !== '1.0') {
		return { ok: false, error: `version must be "1.0" (got ${JSON.stringify(obj.version)})` }
	}
	if (!Array.isArray(obj.fields)) {
		return { ok: false, error: '`fields` must be an array' }
	}
	return { ok: true, value: obj as unknown as FormSchema }
}
